import React from 'react'
import { useTranslation } from 'react-i18next'

import { FieldProps } from '../../../../../../@types/types'
import { ConstructionScreenValidationResult } from '../../../../../../@types/validation-types'
import { TextInput } from '../../../../../common/inputs/textInput/TextInput'
import { cutToChosenDigitLength } from '../../../../../../services/conversion/conversionServices'
import Checkbox from '../../../../../common/inputs/checkbox/Checkbox'
import { getDefaultClasses } from '../../../../../../utils/style-utils'
import ToggleSwitch, { ToggleSwitchAlignment } from '../../../../../common/inputs/toggleSwitch/ToggleSwitch'
import styles from './ConstructionYearField.module.css'

export type ConstructionYearFieldProps = FieldProps<ConstructionScreenValidationResult> & {
  showToggle?: boolean
  toggleLabelTranslationKey?: string
  toggleAlignment?: ToggleSwitchAlignment
  toggleClassName?: string
}

const ConstructionYearField: React.FC<ConstructionYearFieldProps> = ({
  insertionData,
  validationMessages,
  screenName,
  onChange,
  wrapperClassName = getDefaultClasses('leftOnDesk'),
  showToggle = false,
  toggleLabelTranslationKey = 'wizard.screen.construction.unknownConstructionYear.mobile',
  toggleAlignment = 'maxSpread',
  toggleClassName = 'margin-top-m',
}) => {
  const { t } = useTranslation()
  const toggleLabel = t(toggleLabelTranslationKey, 'unknown')
  const renderSwitchFirstToggle = showToggle && toggleAlignment === 'left'

  return (
    <div className={wrapperClassName}>
      <TextInput
        id='construction-year'
        propertyName='constructionYear'
        type='tel' // 'number' type does not trigger the onChange in safari - using 'tel' is the usual workaround!
        numericOnly
        maxLength={4}
        label={<span>{t('wizard.screen.construction.constructionYear', 'Year')}</span>}
        placeholder={t('wizard.screen.construction.constructionYearPlaceholder', 'eg. 1998')}
        value={insertionData.constructionYear}
        errors={validationMessages!.constructionYear ?? []}
        doOnFieldChange={(field, value) =>
          onChange({ screen: screenName, property: field }, cutToChosenDigitLength(value as string, 4))
        }
        disabled={insertionData.unknownConstructionYear}
      />
      <div className={showToggle ? `${toggleClassName} ${renderSwitchFirstToggle ? styles.toggleRow : ''}` : undefined}>
        {renderSwitchFirstToggle ? (
          <>
            <span className={styles.toggleSlot}>
              <ToggleSwitch
                id='unknown-construction-year'
                propertyName='unknownConstructionYear'
                alignment='single'
                label=''
                value={insertionData.unknownConstructionYear ?? false}
                onChange={(_name, checked) =>
                  onChange({ screen: screenName, property: 'unknownConstructionYear' }, checked)
                }
              />
            </span>
            <label
              htmlFor='unknown-construction-year'
              id='unknown-construction-year-label'
              className={`font-body-medium-regular ${styles.toggleText}`}
            >
              {toggleLabel}
            </label>
          </>
        ) : showToggle ? (
          <ToggleSwitch
            id='unknown-construction-year'
            propertyName='unknownConstructionYear'
            alignment={toggleAlignment}
            label={toggleLabel}
            value={insertionData.unknownConstructionYear ?? false}
            onChange={(_name, checked) =>
              onChange({ screen: screenName, property: 'unknownConstructionYear' }, checked)
            }
          />
        ) : (
          <Checkbox
            id='unknown-construction-year'
            onChange={(_name, checked) =>
              onChange({ screen: screenName, property: 'unknownConstructionYear' }, checked)
            }
            name='unknownConstructionYear'
            checked={insertionData.unknownConstructionYear ?? false}
            label={<span>{t('wizard.screen.construction.unknownConstructionYear', 'unknown')}</span>}
          />
        )}
      </div>
    </div>
  )
}

export default ConstructionYearField
